import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Globe, Instagram, Twitter, Youtube, Linkedin, Facebook } from 'lucide-react';
import { message } from 'antd';
import { V, overlayStyle, modalStyle, labelStyle, h2Style } from '../vividGlass';
import brandDnaActions from '../../../../actions/brandDna';

const PLATFORMS = ['instagram', 'twitter', 'facebook', 'youtube', 'linkedin', 'tiktok', 'pinterest'];
const PLATFORM_ICONS = { instagram: Instagram, twitter: Twitter, facebook: Facebook, youtube: Youtube, linkedin: Linkedin };

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
  background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`,
  color: V.text, fontSize: 14, outline: 'none', fontFamily: V.font,
};

export default function EditBrandModal({ brand, brandId, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [socialLinks, setSocialLinks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (brand && open) {
      setForm({
        brand_name: brand.brand_name || '',
        tagline: brand.tagline || '',
        industry: brand.industry || '',
        source_url: brand.source_url || '',
        brand_language: brand.brand_language || '',
        use_emojis: brand.use_emojis ? true : false,
        brand_design_description: brand.brand_design_description || '',
        extra_guidelines: brand.extra_guidelines || '',
      });
      setSocialLinks(
        Array.isArray(brand.social_media_urls)
          ? brand.social_media_urls.filter((s) => s?.platform)
          : []
      );
    }
  }, [brand, open]);

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const addSocialLink = () => setSocialLinks((p) => [...p, { platform: 'instagram', url: '' }]);
  const removeSocialLink = (i) => setSocialLinks((p) => p.filter((_, idx) => idx !== i));
  const updateSocialLink = (i, key, val) =>
    setSocialLinks((p) => p.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

  const handleSave = async () => {
    if (!form.brand_name?.trim()) {
      message.warning('Brand name is required');
      return;
    }
    setSaving(true);
    try {
      await brandDnaActions.updateBrand(brandId, {
        ...form,
        social_media_urls: socialLinks.filter((s) => s.platform && s.url),
      });
      message.success('Brand updated');
      onSaved();
      onClose();
    } catch {
      message.error('Failed to update brand');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={overlayStyle} onClick={onClose}>
          <motion.div
            initial={{ scale: 0.93, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 24 }}
            onClick={(e) => e.stopPropagation()}
            style={{ ...modalStyle, maxWidth: 560 }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ ...h2Style }}>Edit Brand</h2>
              <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
                style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                <X size={14} />
              </motion.button>
            </div>

            {/* Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ ...labelStyle }}>Brand Name *</label>
                <input value={form.brand_name || ''} onChange={(e) => setField('brand_name', e.target.value)} style={inputStyle} placeholder="Your Brand" />
              </div>

              <div>
                <label style={{ ...labelStyle }}>Tagline</label>
                <input value={form.tagline || ''} onChange={(e) => setField('tagline', e.target.value)} style={inputStyle} placeholder="A short brand motto" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ ...labelStyle }}>Industry</label>
                  <input value={form.industry || ''} onChange={(e) => setField('industry', e.target.value)} style={inputStyle} placeholder="e.g. Fashion" />
                </div>
                <div>
                  <label style={{ ...labelStyle }}>Language</label>
                  <input value={form.brand_language || ''} onChange={(e) => setField('brand_language', e.target.value)} style={inputStyle} placeholder="e.g. English" />
                </div>
              </div>

              <div>
                <label style={{ ...labelStyle }}>Website URL</label>
                <div style={{ position: 'relative' }}>
                  <Globe size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: V.textMuted }} />
                  <input value={form.source_url || ''} onChange={(e) => setField('source_url', e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 32 }} placeholder="https://yourbrand.com" />
                </div>
              </div>

              {/* Social links */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ ...labelStyle, marginBottom: 0, color: V.textMuted }}>Social Links</label>
                  <motion.button whileTap={{ scale: 0.95 }} onClick={addSocialLink}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Plus size={11} /> Add
                  </motion.button>
                </div>
                {socialLinks.map((s, i) => {
                  const Icon = PLATFORM_ICONS[s.platform] || Globe;
                  return (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <select
                        value={s.platform}
                        onChange={(e) => updateSocialLink(i, 'platform', e.target.value)}
                        style={{ ...inputStyle, width: 130, flexShrink: 0 }}
                      >
                        {PLATFORMS.map((p) => <option key={p} value={p} style={{ background: '#0c0618' }}>{p}</option>)}
                      </select>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <Icon size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: V.textMuted, pointerEvents: 'none' }} />
                        <input value={s.url} onChange={(e) => updateSocialLink(i, 'url', e.target.value)}
                          placeholder="https://..." style={{ ...inputStyle, paddingLeft: 30 }} />
                      </div>
                      <motion.button whileTap={{ scale: 0.9 }} onClick={() => removeSocialLink(i)}
                        style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <Trash2 size={12} />
                      </motion.button>
                    </div>
                  );
                })}
              </div>

              {/* Emoji toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 12, background: V.glass, border: `1px solid ${V.glassBorder}` }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: V.text }}>Use Emojis</div>
                  <div style={{ fontSize: 11, color: V.textMuted }}>Allow emojis in AI-generated content</div>
                </div>
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setField('use_emojis', !form.use_emojis)}
                  style={{
                    width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
                    background: form.use_emojis ? 'linear-gradient(135deg,#FF7E67,#FF4D8D)' : 'rgba(255,255,255,0.1)',
                    position: 'relative', transition: 'background 0.2s',
                  }}
                >
                  <motion.div
                    animate={{ x: form.use_emojis ? 22 : 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    style={{ position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
                  />
                </motion.div>
              </div>

            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <motion.button whileTap={{ scale: 0.96 }} onClick={onClose}
                style={{ padding: '10px 20px', borderRadius: 12, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 14, cursor: 'pointer', fontFamily: V.font }}>
                Cancel
              </motion.button>
              <motion.button whileTap={{ scale: 0.96 }} onClick={handleSave} disabled={saving}
                style={{ padding: '10px 20px', borderRadius: 12, background: 'linear-gradient(90deg,#FF7E67,#FF4D8D)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: V.font, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
